// One-row tab strips (JDEV-3207): decides which tabs fit in the row.
//
// Strategy "priority" (priority+, the column header ColumnTabs.vue): as many tabs as fit stay in the row, in
// order, and the rest go to the "Lisää (n)" menu (components/ui/TabOverflowMenu.vue). The selected tab is never
// hidden: it takes the place of the last tab that fits. If even the selected tab alone does not fit, it stays
// and truncates (shared/styles .j-tab-label). Markup and CSS: shared/styles/components/tabs.css ("More" bar).
//
//   <div class="j-tabs-bar" ref="bar">                      the available width (a fixed minmax(0, 1fr) grid cell)
//     <div class="j-tabs" role="tablist" ref="list">
//       <… data-tab-id="a">…</…> <… data-tab-id="b" hidden>…</…>    one element per id, hidden when not visible
//     </div>
//     <button class="add" ref="extra">+</button>             optional: always-shown siblings (`reserve`)
//     <TabOverflowMenu> (the toggle, ref passed as `toggle`, margin-inline-start: auto, and the menu)
//   </div>
//
// How the fit works (fitPriority):
// - Every pass measures the real widths: all items are unhidden for a moment (synchronously, so nothing paints),
//   the list is laid out at max-content, and each item's width (plus its inline margins) is read. Then the
//   hidden attributes are put back. Widths therefore follow the language, fonts, counters and close buttons.
// - avail = the bar's content width - the widths of the `reserve` elements.
// - All items fit (sum <= avail): everything is visible and the toggle is hidden.
// - Otherwise the toggle's width (measured with its current count, + 4 px) is reserved too: the selected item
//   is placed first, then the others in order while they fit; the first one that does not fit ends the row
//   (the row stays in order; nothing later is pulled forward).
// - It re-fits after document.fonts.ready and when fonts load later, on ResizeObserver changes of the bar and
//   of the toggle (its count can grow by a digit), and when ids, the selected id or `watch` sources change
//   (labels change with the language). A fit only sets `hidden` on items, which never changes the bar's own
//   size, so it cannot loop.
//
// Truncation (`truncate`, narrow columns): a tab may also shrink until its label is LABEL_MIN_EM wide (the
// selected one SELECTED_MIN_EM), its label ending in an ellipsis. The fit then uses these minimum widths, so
// more tabs stay in the row (all of them if they fit without the toggle) instead of going to the menu. Every
// visible item gets its minimum as an inline min-inline-size; the CSS lets them shrink (data-truncate on the bar).
// Tabs shorter than the minimum keep their own width. ColumnTabs.vue shows a truncated tab's full label.
//
// Strategy "scroll" (JDEV-3208, the touch mode of the editor tabs, components/ui/EditorTabs.vue): every tab
// stays in the list, which scrolls sideways (shared/styles "Scroll strip"):
//
//   <div class="j-tabs-scroller" ref="bar" data-overflow data-fade="start end">
//     <div class="j-tabs" role="tablist" ref="list"> <… data-tab-id="a">…</…> … </div>
//   </div>
//
// - Every id is visible. The bar gets data-overflow while the list overflows, and data-fade with the edges that
//   have hidden tabs ("start", "end" or "start end"; removed when none), which draws the edge fades. They are
//   updated on the list's scroll events, on resizes and font loads, and after every fit.
// - reveal(id) scrolls the list so that tab is fully in view, a fade's width in from the edge where there is room
//   (scroll-padding-inline). The selected tab is revealed instantly on mount, when the strategy switches to
//   "scroll", after resizes (rotation, column width) and after fonts load, and smoothly when the selection
//   changes. prefers-reduced-motion makes every reveal instant.
//
// `strategy` may be a ref: the strategy then switches live (EditorTabs follows usePointerMode). Leaving
// "scroll" removes data-overflow / data-fade and scrolls the list back to the start; leaving "priority"
// removes the truncation minimums. Either way the next fit starts from all tabs visible.

import { isRef, nextTick, onBeforeUnmount, onMounted, ref, unref, watch, type Ref, type WatchSource } from 'vue'

export type TabOverflowStrategy = 'priority' | 'scroll'

export interface TabOverflowOptions {
  /** The bar: its content width is the space for the list, the reserved elements and the toggle. */
  bar: Ref<HTMLElement | null>
  /** The list holding one element per id, marked with data-tab-id. */
  list: Ref<HTMLElement | null>
  /** The "More" toggle. Measured even while hidden. */
  toggle: Ref<HTMLElement | null>
  /** The tab ids, in order. */
  ids: Ref<string[]>
  /** The selected id; always visible. */
  selectedId: Ref<string | null | undefined>
  /** Other always-visible elements in the bar (e.g. an add button); their widths are subtracted. */
  reserve?: Array<Ref<HTMLElement | null>>
  /** Extra re-fit triggers, e.g. the labels (they change with the language). */
  watch?: WatchSource[]
  /** false: show every tab and stop measuring (e.g. the column tabs' button grid, JDEV-3370). */
  enabled?: Ref<boolean>
  /** true: tabs may truncate down to a minimum label width before they go to the menu. */
  truncate?: Ref<boolean>
  /** "priority" (default) or "scroll"; a ref switches live. */
  strategy?: TabOverflowStrategy | Ref<TabOverflowStrategy>
}

export interface TabOverflowResult {
  /** Ids shown in the row, in order. */
  visibleIds: Ref<string[]>
  /** Ids in the menu, in order. */
  hiddenIds: Ref<string[]>
  /** True when at least one tab is hidden (the toggle is shown). */
  overflowing: Ref<boolean>
  /** Measure and fit now (after the DOM has been updated). */
  refit: () => void
  /** Scroll strategy: scroll the list so this tab is fully in view (no-op for "priority"). */
  reveal: (id: string | null | undefined, behavior?: 'smooth' | 'instant') => void
}

const TOGGLE_SLACK = 4 // px, as the prototype: rounding and the toggle's own focus ring
// Minimum label widths when truncating, in em of the list's font: about 5 letters plus the ellipsis (and 8 for
// the selected tab), so a truncated tab still reads as its name.
export const LABEL_MIN_EM = 4
export const SELECTED_MIN_EM = 6

function outerWidth (el: Element) {
  const cs = getComputedStyle(el)
  return el.getBoundingClientRect().width + (parseFloat(cs.marginInlineStart) || 0) + (parseFloat(cs.marginInlineEnd) || 0)
}

function contentWidth (el: HTMLElement) {
  const cs = getComputedStyle(el)
  return el.clientWidth - (parseFloat(cs.paddingInlineStart) || 0) - (parseFloat(cs.paddingInlineEnd) || 0)
}

// Reads natural widths with everything shown, then restores the DOM exactly as it was.
function measure (list: HTMLElement, ids: string[], toggle: HTMLElement | null) {
  const items = ids.map(id => list.querySelector<HTMLElement>(`[data-tab-id="${CSS.escape(id)}"]`))
  const wasHidden = items.map(el => !!el && el.hidden)
  const toggleWasHidden = !!toggle && toggle.hidden
  const { flex, width, maxWidth } = list.style
  items.forEach(el => { if (el) el.hidden = false })
  if (toggle) toggle.hidden = false
  list.style.flex = '0 0 auto'
  list.style.width = 'max-content'
  list.style.maxWidth = 'none'
  items.forEach(el => { if (el) el.style.minInlineSize = '' })
  const widths = items.map(el => (el ? Math.ceil(outerWidth(el)) : 0))
  const labelWidths = items.map(el => {
    const label = el?.querySelector<HTMLElement>('.j-tab-label')
    return label ? Math.ceil(label.getBoundingClientRect().width) : 0
  })
  // Border box only: the toggle's margin-inline-start is `auto` (it pins the toggle to the end), and
  // getComputedStyle reports an auto margin as the space it currently takes.
  const toggleWidth = toggle ? Math.ceil(toggle.getBoundingClientRect().width) + TOGGLE_SLACK : 0
  list.style.flex = flex
  list.style.width = width
  list.style.maxWidth = maxWidth
  items.forEach((el, i) => { if (el) el.hidden = wasHidden[i] })
  if (toggle) toggle.hidden = toggleWasHidden
  return { items, widths, labelWidths, toggleWidth }
}

/** Greedy priority+ fit. Pure: widths in, visible ids out. Exported for tests. */
export function fitPriority (ids: string[], natural: number[], avail: number, toggleWidth: number, selectedId?: string | null, minWidths?: number[] | null) {
  const sum = (a: number[]) => a.reduce((x, y) => x + y, 0)
  if (sum(natural) <= avail) return ids.slice()
  if (minWidths && sum(minWidths) <= avail) return ids.slice()
  const widths = minWidths || natural
  const show = new Set<string>()
  const sel = selectedId != null ? ids.indexOf(selectedId) : -1
  let used = 0
  if (sel >= 0) {
    show.add(ids[sel])
    used = widths[sel]
  }
  for (let i = 0; i < ids.length; i++) {
    if (i === sel) continue
    if (used + widths[i] + toggleWidth > avail) break
    show.add(ids[i])
    used += widths[i]
  }
  return ids.filter(id => show.has(id))
}

// Scroll strategy: which edges of the list have tabs scrolled out of view. scrollLeft is 0..-max in RTL.
function updateScrollEdges (bar: HTMLElement, list: HTMLElement) {
  const max = list.scrollWidth - list.clientWidth
  const over = max > 1
  const fromStart = Math.abs(list.scrollLeft)
  const fade = [over && fromStart > 1 ? 'start' : '', over && fromStart < max - 1 ? 'end' : ''].filter(Boolean).join(' ')
  bar.toggleAttribute('data-overflow', over)
  if (fade) {
    if (bar.getAttribute('data-fade') !== fade) bar.setAttribute('data-fade', fade)
  } else bar.removeAttribute('data-fade')
}

function clearScrollEdges (bar: HTMLElement | null) {
  bar?.removeAttribute('data-overflow')
  bar?.removeAttribute('data-fade')
}

const prefersReducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

const strategies: Record<TabOverflowStrategy, (o: TabOverflowOptions) => string[] | null> = {
  scroll (o) {
    const bar = o.bar.value
    const list = o.list.value
    if (!bar || !list) return null
    updateScrollEdges(bar, list)
    return o.ids.value.slice()
  },
  priority (o) {
    const bar = o.bar.value
    const list = o.list.value
    if (!bar || !list) return null
    const ids = o.ids.value
    const { items, widths, labelWidths, toggleWidth } = measure(list, ids, o.toggle.value)
    const reserved = (o.reserve || []).reduce((sum, r) => sum + (r.value && !r.value.hidden ? Math.ceil(outerWidth(r.value)) : 0), 0)
    const avail = contentWidth(bar) - reserved
    const truncate = !!o.truncate?.value
    let mins: number[] | null = null
    if (truncate) {
      const em = parseFloat(getComputedStyle(list).fontSize) || 16
      mins = widths.map((w, i) => {
        const minLabel = Math.ceil(em * (ids[i] === o.selectedId.value ? SELECTED_MIN_EM : LABEL_MIN_EM))
        return w - labelWidths[i] + Math.min(labelWidths[i], minLabel)
      })
    }
    const visible = fitPriority(ids, widths, avail, toggleWidth, o.selectedId.value, mins)
    if (mins) {
      // The selected tab alone may not fit even at its minimum: cap its minimum to the room left, so it
      // truncates further instead of running under the toggle.
      const shown = new Set(visible)
      const sel = ids.indexOf(o.selectedId.value as string)
      if (sel >= 0) {
        const others = ids.reduce((sum, id, i) => sum + (i !== sel && shown.has(id) ? mins![i] : 0), 0)
        const room = avail - others - (visible.length < ids.length ? toggleWidth : 0)
        mins[sel] = Math.max(0, Math.min(mins[sel], room))
      }
    }
    items.forEach((el, i) => { if (el) el.style.minInlineSize = mins ? mins[i] + 'px' : '' })
    bar.toggleAttribute('data-truncate', truncate)
    return visible
  }
}

export function useTabOverflow (options: TabOverflowOptions): TabOverflowResult {
  const strategyName = () => unref(options.strategy) || 'priority'
  const visibleIds = ref<string[]>(options.ids.value.slice())
  const hiddenIds = ref<string[]>([])
  const overflowing = ref(false)
  const enabled = () => !options.enabled || options.enabled.value
  let alive = true

  function apply (visible: string[]) {
    const shown = new Set(visible)
    const hidden = options.ids.value.filter(id => !shown.has(id))
    // Only replace the arrays when they change, so watchers and renders stay quiet on resize.
    const changed = visible.join('\u0000') !== visibleIds.value.join('\u0000')
    if (changed) visibleIds.value = visible
    if (hidden.join('\u0000') !== hiddenIds.value.join('\u0000')) hiddenIds.value = hidden
    overflowing.value = hidden.length > 0
    return changed
  }

  function fitNow () {
    if (!enabled()) {
      // Every tab shown (layout edit mode, the column tabs' button grid of JDEV-3370): no truncation minimums.
      const list = options.list.value
      list?.querySelectorAll<HTMLElement>('[data-tab-id]').forEach(el => { el.style.minInlineSize = '' })
      options.bar.value?.removeAttribute('data-truncate')
      apply(options.ids.value.slice())
      return
    }
    const name = strategyName()
    const visible = strategies[name](options)
    // Scroll: tabs that the previous strategy hid are shown only after the next render; measure the edges again then.
    if (visible && apply(visible) && name === 'scroll') refit()
  }

  function reveal (id: string | null | undefined, behavior: 'smooth' | 'instant' = 'smooth') {
    const bar = options.bar.value
    const list = options.list.value
    if (strategyName() !== 'scroll' || !enabled() || !bar || !list || id == null) return
    const el = list.querySelector<HTMLElement>(`[data-tab-id="${CSS.escape(id)}"]`)
    if (!el || el.hidden) return
    const lr = list.getBoundingClientRect()
    const r = el.getBoundingClientRect()
    // A fade's width of room on the side it comes from (the fade would cover it), less when the tab is wide.
    const fade = parseFloat(getComputedStyle(bar).getPropertyValue('--j-tabs-fade-size')) || 32
    const pad = Math.max(0, Math.min(fade, (lr.width - r.width) / 2))
    let dx = 0
    if (r.width > lr.width) dx = (getComputedStyle(list).direction === 'rtl' ? r.right - lr.right : r.left - lr.left)
    else if (r.left < lr.left + pad) dx = r.left - lr.left - pad
    else if (r.right > lr.right - pad) dx = r.right - lr.right + pad
    if (Math.abs(dx) < 1) return
    list.scrollBy({ left: dx, behavior: behavior === 'instant' || prefersReducedMotion() ? 'instant' : 'smooth' })
  }

  // Reveal the selected tab once the current fit has run (scroll strategy).
  let revealQueued = false
  function revealSelectedSoon () {
    if (revealQueued || !alive) return
    revealQueued = true
    nextTick(() => requestAnimationFrame(() => requestAnimationFrame(() => {
      revealQueued = false
      if (alive) reveal(options.selectedId.value, 'instant')
    })))
  }

  let queued = false
  function refit () {
    if (queued || !alive) return
    queued = true
    // After Vue has patched the DOM (new tabs, labels, selection), and once per frame at most.
    nextTick(() => requestAnimationFrame(() => {
      queued = false
      if (alive) fitNow()
    }))
  }

  // The bar follows the column width (column resize, side menu, rotation); the toggle grows when its count
  // gains a digit, which takes room from the tabs.
  // Scroll: after a resize or rotation the selected tab is brought back into view.
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
    refit()
    if (strategyName() === 'scroll') revealSelectedSoon()
  })
  watch([options.bar, options.toggle], ([bar, toggle], old) => {
    for (const el of old || []) if (el) observer?.unobserve(el)
    if (bar) observer?.observe(bar)
    if (toggle) observer?.observe(toggle)
  }, { immediate: true, flush: 'post' })

  const onFonts = () => {
    refit()
    if (strategyName() === 'scroll') revealSelectedSoon()
  }
  // Scroll: the fades follow the scroll position.
  const onScroll = () => {
    const bar = options.bar.value
    const list = options.list.value
    if (bar && list && strategyName() === 'scroll' && enabled()) updateScrollEdges(bar, list)
  }
  watch(options.list, (list, old) => {
    old?.removeEventListener('scroll', onScroll)
    list?.addEventListener('scroll', onScroll, { passive: true })
  }, { immediate: true, flush: 'post' })

  if (isRef(options.strategy)) {
    watch(options.strategy, (name, old) => {
      const bar = options.bar.value
      const list = options.list.value
      if (old === 'scroll') {
        clearScrollEdges(bar)
        list?.scrollTo({ left: 0, behavior: 'instant' })
      }
      if (old === 'priority' && list) {
        list.querySelectorAll<HTMLElement>('[data-tab-id]').forEach(el => { el.style.minInlineSize = '' })
        bar?.removeAttribute('data-truncate')
      }
      // Start the new strategy from every tab shown, as on mount.
      apply(options.ids.value.slice())
      refit()
      if (name === 'scroll') revealSelectedSoon()
    }, { flush: 'post' })
  }

  onMounted(() => {
    fitNow()
    if (strategyName() === 'scroll') revealSelectedSoon()
    if (document.fonts) {
      document.fonts.ready.then(onFonts).catch(() => {})
      document.fonts.addEventListener?.('loadingdone', onFonts)
    }
  })
  onBeforeUnmount(() => {
    alive = false
    observer?.disconnect()
    options.list.value?.removeEventListener('scroll', onScroll)
    document.fonts?.removeEventListener?.('loadingdone', onFonts)
  })

  watch(
    [options.ids, options.selectedId, ...(options.enabled ? [options.enabled] : []), ...(options.truncate ? [options.truncate] : []), ...(options.watch || [])],
    () => {
      // Keep the selected tab in the row right away (no frame with the selected tab hidden), then measure.
      const sel = options.selectedId.value
      const visible = visibleIds.value.filter(id => options.ids.value.includes(id))
      if (sel && options.ids.value.includes(sel) && !visible.includes(sel)) visible.push(sel)
      apply(options.ids.value.filter(id => visible.includes(id) || !enabled()))
      refit()
    },
    { deep: true }
  )

  // Scroll: a newly selected tab slides into view.
  watch(options.selectedId, (id) => {
    if (strategyName() !== 'scroll') return
    nextTick(() => requestAnimationFrame(() => { if (alive) reveal(id, 'smooth') }))
  }, { flush: 'post' })

  return { visibleIds, hiddenIds, overflowing, refit, reveal }
}
